import {
  MAX_LIVE_DEGREE,
  RELATIONSHIP_LIMIT,
  beginLeafDetail,
  definitionList,
  deferredRelationshipMessage,
  displayValue,
  entityLink,
  escapeHtml,
  formatCount,
  formatCurrency,
  formatDate,
  formatPercent,
  graphql,
  listFooter,
  PremiumRequiredError,
  premiumRequiredHtml,
  relationshipTable,
  renderDetailError,
  renderPremiumRequired,
  renderDetailPage
} from './leaf-detail';
import { registerGraphTarget } from './graph-target';
import {
  VENDOR_RANKED_CLIENTS_QUERY,
  liveClientList,
  loadMoneyFlow,
  rankedClientList,
  vendorFlow,
  type ClientList,
  type RankedClientsData,
} from './vendor-clients';
import { blockIdFromGeoid, isBlockGeoid } from './block-id';
import { committeeTypeLabel, individualCommitteeFlow } from './individual-committees';
import { currentCycle } from './cycle-facet';
import { districtsHtml, locationHtml, overviewHtml } from './individual-profile';
import type { DonorPlace, DonorPostalCode } from './individual-profile';
import { fetchPurposeFlow, mountVendorPurposes, purposeSummary } from './vendor-purposes';

type Scalar = string | number | null;

interface Connection<TNode, TProperties = Record<string, never>> {
  edges: Array<{ node: TNode; properties: TProperties }>;
}

interface CommitteeNode {
  id: string;
  committee_name: string | null;
  committee_type: string | null;
  state: string | null;
}

interface IndividualNode {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  employer: string | null;
  occupation: string | null;
  total_contributions: Scalar;
  contribution_count: Scalar;
  committee_count: Scalar;
  first_contribution: string | null;
  last_contribution: string | null;
  cycles: string | null;
  party_affinity_dem?: Scalar;
  party_affinity_rep?: Scalar;
  employedByEmployersConnection: Connection<EmployerNode, {
    occupation: string | null;
    record_count: Scalar;
    cycles: string | null;
  }>;
  residesInPlacesConnection: { edges: Array<{ node: DonorPlace }> };
  residesInPostalCodesConnection: { edges: Array<{ node: DonorPostalCode }> };
}

interface EmployerNode {
  id: string;
  name: string | null;
  name_normalized: string | null;
  total_employee_contributions: Scalar;
  employee_donor_count: Scalar;
  contribution_count: Scalar;
  committee_count: Scalar;
  first_contribution: string | null;
  last_contribution: string | null;
  cycles: string | null;
}

interface EmployerDonorData extends EmployerNode {
  individualsEmployedByConnection: Connection<IndividualNode, {
    occupation: string | null;
    record_count: Scalar;
    cycles: string | null;
  }>;
}

interface VendorNode {
  id: string;
  name: string | null;
  name_normalized: string | null;
  city: string | null;
  state: string | null;
  total_received: Scalar;
  payment_count: Scalar;
  purpose_categories: string | null;
  client_count: Scalar;
  first_payment: string | null;
  last_payment: string | null;
  cycles: string | null;
}

interface VendorClientData extends VendorNode {
  committeesPaysConnection: Connection<CommitteeNode & { party: string | null; party_affiliation: string | null }, {
    total_amount: Scalar;
    payment_count: Scalar;
    purposes: string | null;
    categories: string | null;
    cycles: string | null;
  }>;
}

interface CountyNode {
  id: string;
  name: string | null;
  state: string | null;
  state_fips: string | null;
  county_fips: string | null;
}

interface DistrictNode {
  id: string;
  name: string | null;
  state: string | null;
  district_number: string | null;
}

interface BlockNode {
  id: string;
  geoid: string | null;
  block: string | null;
  state: string | null;
  state_fips: string | null;
  tract: string | null;
  county_fips: string | null;
  geoid_county: string | null;
  reg_total: Scalar;
  reg_dem: Scalar;
  reg_rep: Scalar;
  reg_ind: Scalar;
  reg_oth: Scalar;
  reg_male: Scalar;
  reg_female: Scalar;
  reg_white: Scalar;
  reg_hispanic: Scalar;
  reg_black: Scalar;
  reg_asian: Scalar;
  gen2022_voted: Scalar;
  gen2022_turnout_pct: Scalar;
  gen2022_voted_white: Scalar;
  gen2022_voted_hispanic: Scalar;
  gen2022_voted_black: Scalar;
  gen2022_voted_asian: Scalar;
  gen2022_turnout_pct_white: Scalar;
  gen2022_turnout_pct_hispanic: Scalar;
  gen2022_turnout_pct_black: Scalar;
  gen2022_turnout_pct_asian: Scalar;
  gen2022_voted_male: Scalar;
  gen2022_voted_female: Scalar;
  gen2022_turnout_pct_male: Scalar;
  gen2022_turnout_pct_female: Scalar;
  inCountyCountiesConnection: Connection<CountyNode>;
  inDistrictDistrictsConnection: Connection<DistrictNode>;
}

interface PrecinctNode {
  id: string;
  geoid: string | null;
  state: string | null;
  state_fips: string | null;
  county_fips: string | null;
  precinct_id: string | null;
  votes_dem: Scalar;
  votes_rep: Scalar;
  votes_total: Scalar;
  pct_dem_lead: Scalar;
  official_boundary: boolean | null;
  inCountyCountiesConnection: Connection<CountyNode>;
}

// Fixed documents (persisted-query list, sites#277): first: 20 is RELATIONSHIP_LIMIT.
// The donor ZIP's district overlaps are congressional only: since rundeck#831 a ZIP also OVERLAPS its State
// Senate and State House districts ({st}-sd-{code}, {st}-hd-{code}; sites#544).
const INDIVIDUAL_QUERY = `
  query IndividualDetail($id: String!) {
    individuals(where: { id: { eq: $id } }, limit: 1) {
      id name city state zip_code employer occupation
      total_contributions contribution_count committee_count
      first_contribution last_contribution cycles party_affinity_dem party_affinity_rep
      employedByEmployersConnection(first: 20) {
        edges {
          properties { occupation record_count cycles }
          node {
            id name name_normalized total_employee_contributions employee_donor_count
            contribution_count committee_count first_contribution last_contribution cycles
          }
        }
      }
      residesInPlacesConnection(first: 20) {
        edges { node { id name name_normalized state } }
      }
      residesInPostalCodesConnection(first: 20) {
        edges {
          node {
            id zip_code
            overlapsCountiesConnection(first: 20, sort: [{ edge: { overlap_land_area: DESC } }]) {
              edges { properties { overlap_land_area } node { id name state } }
            }
            overlapsDistrictsConnection(
              first: 20
              sort: [{ edge: { overlap_land_area: DESC } }]
              where: { NOT: { node: { OR: [{ district_slug: { contains: "-sd-" } }, { district_slug: { contains: "-hd-" } }] } } }
            ) {
              edges { properties { overlap_land_area } node { id name state district_number district_slug } }
            }
          }
        }
      }
    }
  }
`;

const EMPLOYER_PROFILE_QUERY = `
  query EmployerProfile($id: String!) {
    employers(where: { id: { eq: $id } }, limit: 1) {
      id name name_normalized total_employee_contributions employee_donor_count
      contribution_count committee_count first_contribution last_contribution cycles
    }
  }
`;

const EMPLOYER_DONORS_QUERY = `
  query EmployerDonors($id: String!) {
    employers(where: { id: { eq: $id } }, limit: 1) {
      id name name_normalized total_employee_contributions employee_donor_count
      contribution_count committee_count first_contribution last_contribution cycles
      individualsEmployedByConnection(
        first: 20
        sort: [{ node: { total_contributions: DESC } }]
      ) {
        edges {
          properties { occupation record_count cycles }
          node {
            id name city state zip_code employer occupation total_contributions
            contribution_count committee_count first_contribution last_contribution cycles
          }
        }
      }
    }
  }
`;

const VENDOR_PROFILE_QUERY = `
  query VendorProfile($id: String!) {
    vendors(where: { id: { eq: $id } }, limit: 1) {
      id name name_normalized city state total_received payment_count
      purpose_categories client_count first_payment last_payment cycles
    }
  }
`;

// Legacy edge-sorted clients connection, for an api-graphql without moneyCounterparties (sites#194). Remove with
// INDIVIDUAL_COMMITTEES_LEGACY_QUERY.
const VENDOR_CLIENTS_QUERY = `
  query VendorClients($id: String!) {
    vendors(where: { id: { eq: $id } }, limit: 1) {
      id name name_normalized city state total_received payment_count
      purpose_categories client_count first_payment last_payment cycles
      committeesPaysConnection(
        first: 20
        sort: [{ edge: { total_amount: DESC } }]
      ) {
        edges {
          properties { total_amount payment_count purposes categories cycles }
          node { id committee_name committee_type state party party_affiliation }
        }
      }
    }
  }
`;

/**
 * Per-counterparty money lists (api-graphql moneyCounterparties, sites#194):
 * one row per committee summed over its edges, and totalCount = distinct
 * committees, so the lists stay right once rundeck#758 splits money edges per
 * cycle. The resolver reads from the entity and reports `deferred` past
 * MAX_LIVE_DEGREE edges.
 */
interface MoneyCounterpartyRow {
  id: string;
  name: string | null;
  committeeType: string | null;
  party?: string | null;
  affinityDem?: number | null;
  affinityRep?: number | null;
  /** CONTRIBUTED_TO: net of paired refunds (rundeck#792 D3); other types: the summed amount. */
  amount: number | null;
  /** CONTRIBUTED_TO only: gross and paired refunds beside the net. */
  gross?: number | null;
  refunded?: number | null;
  count: number | null;
  purposes?: string | null;
}

interface MoneyCounterpartiesData {
  moneyCounterparties: {
    deferred: boolean | null;
    totalCount: number | null;
    /** CONTRIBUTED_TO: committees whose net is not positive, listed as a group, not as rows. */
    refundedInFullCount?: number | null;
    counterparties: MoneyCounterpartyRow[] | null;
  } | null;
}

const INDIVIDUAL_COMMITTEES_QUERY = `
  query IndividualCommittees($id: String!, $cycle: Int) {
    moneyCounterparties(entityId: $id, relationship: CONTRIBUTED_TO, direction: OUT, first: 20, cycle: $cycle) {
      deferred totalCount refundedInFullCount
      counterparties { id name committeeType party affinityDem affinityRep amount gross refunded count }
    }
  }
`;

const VENDOR_COUNTERPARTY_CLIENTS_QUERY = `
  query VendorClientList($id: String!, $cycle: Int) {
    moneyCounterparties(entityId: $id, relationship: PAYS, direction: IN, first: 20, cycle: $cycle) {
      deferred totalCount
      counterparties { id name committeeType party affinityDem affinityRep amount count purposes }
    }
  }
`;

// The per-edge connections moneyCounterparties replaces, for an api-graphql
// that does not serve it yet. Remove once every API the page is served with has it.
const INDIVIDUAL_COMMITTEES_LEGACY_QUERY = `
  query IndividualCommitteesLegacy($id: String!) {
    individuals(where: { id: { eq: $id } }, limit: 1) {
      contributedToCommitteesConnection(
        first: 20
        sort: [{ edge: { total_amount: DESC } }]
      ) {
        edges {
          properties { total_amount contribution_count }
          node { id committee_name committee_type }
        }
      }
    }
  }
`;

interface IndividualCommitteesLegacyData {
  individuals: Array<{
    contributedToCommitteesConnection: Connection<
      Pick<CommitteeNode, 'id' | 'committee_name' | 'committee_type'>,
      { total_amount: Scalar; contribution_count: Scalar }
    >;
  }>;
}

const BLOCK_QUERY = `
  query BlockDetail($id: String!) {
    blocks(where: { id: { eq: $id } }, limit: 1) {
      id geoid block state state_fips tract county_fips geoid_county
      reg_total reg_dem reg_rep reg_ind reg_oth reg_male reg_female
      reg_white reg_hispanic reg_black reg_asian
      gen2022_voted gen2022_turnout_pct
      gen2022_voted_white gen2022_voted_hispanic gen2022_voted_black gen2022_voted_asian
      gen2022_turnout_pct_white gen2022_turnout_pct_hispanic
      gen2022_turnout_pct_black gen2022_turnout_pct_asian
      gen2022_voted_male gen2022_voted_female
      gen2022_turnout_pct_male gen2022_turnout_pct_female
      inCountyCountiesConnection(first: 20) {
        edges { node { id name state state_fips county_fips } }
      }
      inDistrictDistrictsConnection(first: 20) {
        edges { node { id name state district_number } }
      }
    }
  }
`;

const PRECINCT_QUERY = `
  query PrecinctDetail($id: String!) {
    precincts(where: { id: { eq: $id } }, limit: 1) {
      id geoid state state_fips county_fips precinct_id
      votes_dem votes_rep votes_total pct_dem_lead official_boundary
      inCountyCountiesConnection(first: 20) {
        edges { node { id name state state_fips county_fips } }
      }
    }
  }
`;

async function loadLeafEntity<T>(
  collection: string,
  id: string,
  query: string,
  responseKey: string
): Promise<T | null> {
  const resolution = beginLeafDetail(collection, id);
  const fetchOne = async (candidateId: string): Promise<T | null> => {
    const data = await graphql<Record<string, T[]>>(query, { id: candidateId });
    return data[responseKey]?.[0] || null;
  };

  const direct = await fetchOne(id);
  if (direct) return direct;

  const resolvedId = await resolution;
  return resolvedId && resolvedId !== id ? fetchOne(resolvedId) : null;
}

interface CommitteeList {
  rows: MoneyCounterpartyRow[];
  /** Distinct committees, for the footer; null when unknown. */
  total: number | null;
  /** Committees refunded in full (net not positive), shown as a collapsed group (rundeck#792 D3). */
  refundedInFull?: number | null;
}

/** null: deferred (more edges than a live read allows). */
function counterpartyList(data: MoneyCounterpartiesData): CommitteeList | null {
  const list = data.moneyCounterparties;
  if (list?.deferred) return null;
  return {
    rows: list?.counterparties || [],
    total: list?.totalCount ?? null,
    refundedInFull: list?.refundedInFullCount ?? null,
  };
}

/**
 * Top Committees of an individual (sites#194): moneyCounterparties for the
 * selected cycle (or all), else the legacy edge-sorted connection, which has no
 * cycle and so is used only for all cycles.
 */
async function loadIndividualCommittees(individual: IndividualNode): Promise<CommitteeList | null> {
  const cycle = currentCycle();
  try {
    return counterpartyList(await graphql<MoneyCounterpartiesData>(INDIVIDUAL_COMMITTEES_QUERY, { id: individual.id, cycle }));
  } catch (error) {
    // moneyCounterparties not deployed: one edge per pair until rundeck#758, so the edges are the committees.
    if (cycle) throw error;
  }
  const legacy = await graphql<IndividualCommitteesLegacyData>(INDIVIDUAL_COMMITTEES_LEGACY_QUERY, { id: individual.id });
  const edges = legacy.individuals[0]?.contributedToCommitteesConnection.edges || [];
  return {
    rows: edges.map(({ node, properties }) => ({
      id: node.id,
      name: node.committee_name,
      committeeType: node.committee_type,
      amount: properties.total_amount == null ? null : Number(properties.total_amount),
      count: properties.contribution_count == null ? null : Number(properties.contribution_count),
    })),
    total: individual.committee_count == null || individual.committee_count === '' ? null : Number(individual.committee_count),
  };
}

/**
 * Gross / Refunded / Net per committee, sorted by net (rundeck#792 D3). Rows from an API
 * without the net columns show the gross as the amount. Committees refunded in full are a
 * collapsed count below the list, not hidden.
 */
function individualCommitteesHtml(list: CommitteeList | null, individual: IndividualNode): string {
  if (!list) return deferredRelationshipMessage(Number(individual.committee_count || 0));
  const type = (row: MoneyCounterpartyRow) => {
    const label = committeeTypeLabel(row.committeeType);
    return label ? `<span title="FEC committee type ${escapeHtml(row.committeeType)}">${escapeHtml(label)}</span>` : displayValue(null);
  };
  // Gross and Net are the same column until something was refunded.
  const refunds = list.rows.some(row => Number(row.refunded) > 0);
  const table = refunds
    ? relationshipTable(['Committee', 'Type', 'Gross', 'Refunded', 'Net', 'Contributions'], list.rows.map(row => [
      entityLink('Committee', row.id, row.name || 'Unknown committee'),
      type(row),
      formatCurrency(row.gross ?? row.amount),
      row.refunded == null ? '<span class="meta">—</span>' : formatCurrency(row.refunded),
      formatCurrency(row.amount),
      formatCount(row.count)
    ]), [2, 3, 4, 5])
    : relationshipTable(['Committee', 'Type', 'Amount', 'Contributions'], list.rows.map(row => [
      entityLink('Committee', row.id, row.name || 'Unknown committee'),
      type(row),
      formatCurrency(row.amount),
      formatCount(row.count)
    ]), [2, 3]);
  const refunded = list.refundedInFull && list.refundedInFull > 0
    ? `<p class="meta leaf-refunded-in-full">${list.refundedInFull === 1 ? '1 committee' : `${list.refundedInFull} committees`} refunded in full (net $0) not listed.</p>`
    : '';
  return list.rows.length ? `${table}${listFooter(list.rows.length, list.total, 'committees')}${refunded}` : `${table}${refunded}`;
}

/** Sankey | Table toggle on the committees table (sites#413); on any failure the table stays as it is. */
async function mountIndividualSankey(individual: IndividualNode, list: CommitteeList | null): Promise<void> {
  const section = document.getElementById('committees');
  const heading = section?.querySelector<HTMLElement>('h2');
  const table = section?.querySelector<HTMLElement>('table.leaf-relationship-list');
  if (!list || list.rows.length < 2 || !section || !heading || !table) return;
  const { nodes, links, widened } = individualCommitteeFlow({ id: individual.id, name: individual.name || 'Donor' }, list.rows);
  if (!links.length) return;
  const note = widened ? 'Small contributions are drawn wider than scale so each committee stays visible; hover for exact amounts.' : undefined;
  try {
    await loadMoneyFlow();
    window.electinfoMoneyFlow?.mount({ section, heading, list: table, nodes, links, side: 'out', note });
  } catch (err) {
    console.warn('individual committees Sankey not rendered', err);
  }
}

export async function loadIndividualDetail(id: string): Promise<void> {
  try {
    const individual = await loadLeafEntity<IndividualNode>('individuals', id, INDIVIDUAL_QUERY, 'individuals');
    if (!individual) return renderDetailError('Individual', 'No individual donor was found with this ID.');

    const committees = await loadIndividualCommittees(individual);
    const districts = districtsHtml(individual);
    const employerRows = individual.employedByEmployersConnection.edges.map(({ node, properties }) => [
      entityLink('Employer', node.id, node.name || 'Unknown employer'),
      displayValue(properties.occupation),
      formatCount(properties.record_count)
    ]);
    renderDetailPage(individual.name || `Individual ${individual.id}`, 'Individual Donor', [
      { id: 'overview', title: 'Overview', html: overviewHtml(individual) },
      { id: 'location', title: 'Location', html: locationHtml(individual) },
      ...(districts ? [{ id: 'districts', title: 'Districts', html: districts }] : []),
      { id: 'activity', title: 'Contribution Activity', html: definitionList([
        ['Total contributed', formatCurrency(individual.total_contributions)],
        ['Contributions', formatCount(individual.contribution_count)], ['Committees', formatCount(individual.committee_count)]
      ]) },
      { id: 'committees', title: 'Top Committees', html: individualCommitteesHtml(committees, individual) },
      { id: 'employment', title: `Employers (up to ${RELATIONSHIP_LIMIT})`, html: relationshipTable(
        ['Employer', 'Occupation', 'Records'], employerRows, [2]
      ) }
    ], 'individuals', 'Browse Individual Donors');
    void mountIndividualSankey(individual, committees);
    registerGraphTarget({ kind: 'node', label: 'Individual', id: individual.id, title: individual.name || `Individual ${individual.id}` });
  } catch (error) {
    if (error instanceof PremiumRequiredError) return renderPremiumRequired('Individual Donor', 'Individual donor records');
    renderDetailError('Individual', errorMessage(error));
  }
}

export async function loadEmployerDetail(id: string): Promise<void> {
  try {
    const profile = await loadLeafEntity<EmployerNode>('employers', id, EMPLOYER_PROFILE_QUERY, 'employers');
    if (!profile) return renderDetailError('Employer', 'No employer was found with this ID.');

    const degree = Number(profile.employee_donor_count || 0);
    let donorsHtml: string;
    if (degree < MAX_LIVE_DEGREE) {
      // rundeck#771 can replace only this loader with rank/dst_id filtering.
      try {
        const donorData = await graphql<{ employers: EmployerDonorData[] }>(EMPLOYER_DONORS_QUERY, { id: profile.id });
        const donors = donorData.employers[0]?.individualsEmployedByConnection.edges || [];
        donorsHtml = relationshipTable(['Individual', 'Occupation', 'Donor total', 'Records'], donors.map(({ node, properties }) => [
          entityLink('Individual', node.id, node.name || 'Unknown donor'),
          displayValue(properties.occupation || node.occupation),
          formatCurrency(node.total_contributions),
          formatCount(properties.record_count)
        ]), [2, 3]);
      } catch (error) {
        // Donor names are premium (sites#277); the employer's totals stay public.
        if (!(error instanceof PremiumRequiredError)) throw error;
        donorsHtml = premiumRequiredHtml('Employee donor names');
      }
    } else {
      donorsHtml = deferredRelationshipMessage(degree);
    }

    renderDetailPage(profile.name || `Employer ${profile.id}`, 'Employer Organization', [
      { id: 'overview', title: 'Overview', html: definitionList([
        ['Name', profile.name], ['Normalized name', profile.name_normalized], ['Cycles', profile.cycles],
        ['First contribution', formatDate(profile.first_contribution)], ['Last contribution', formatDate(profile.last_contribution)]
      ]) },
      { id: 'activity', title: 'Employee Contribution Activity', html: definitionList([
        ['Total contributed', formatCurrency(profile.total_employee_contributions)],
        ['Employee donors', formatCount(profile.employee_donor_count)],
        ['Contributions', formatCount(profile.contribution_count)], ['Committees', formatCount(profile.committee_count)]
      ]) },
      { id: 'donors', title: `Top Employee Donors (up to ${RELATIONSHIP_LIMIT})`, html: donorsHtml }
    ], 'employers', 'Browse Employers');
  } catch (error) {
    renderDetailError('Employer', errorMessage(error));
  }
}

/**
 * Top Committee Clients (sites#261): the ranked PAYS list when the graph has
 * it (rundeck#771, any client count), else the live per-committee list
 * (moneyCounterparties, sites#194) below MAX_LIVE_DEGREE, else the deferred
 * message.
 */
async function loadVendorClients(profile: VendorNode): Promise<ClientList | null> {
  // The cycle (sites#194) scopes the ranked and live lists; the legacy list has none.
  const cycle = currentCycle();
  try {
    const ranked = rankedClientList(await graphql<RankedClientsData>(VENDOR_RANKED_CLIENTS_QUERY, { id: profile.id, cycle }));
    if (ranked) return ranked;
  } catch {
    // vendorClients not deployed or failed: same as no ranked data.
  }
  if (Number(profile.client_count || 0) >= MAX_LIVE_DEGREE) return null;
  try {
    const list = counterpartyList(await graphql<MoneyCounterpartiesData>(VENDOR_COUNTERPARTY_CLIENTS_QUERY, { id: profile.id, cycle }));
    if (!list) return null;
    // P is the distinct committees the list was cut from.
    return liveClientList(list.rows.map(row => ({
      id: row.id,
      name: row.name,
      committeeType: row.committeeType,
      party: row.party ?? null,
      affinityDem: row.affinityDem ?? null,
      affinityRep: row.affinityRep ?? null,
      amount: row.amount,
      count: row.count,
      purposes: row.purposes ?? null,
    })), list.total, profile.total_received);
  } catch (error) {
    // moneyCounterparties not deployed: the legacy edge-sorted connection below.
    if (cycle) throw error;
  }
  const clientData = await graphql<{ vendors: VendorClientData[] }>(VENDOR_CLIENTS_QUERY, { id: profile.id });
  const edges = clientData.vendors[0]?.committeesPaysConnection.edges || [];
  return liveClientList(edges.map(({ node, properties }) => ({
    id: node.id,
    name: node.committee_name,
    committeeType: node.committee_type,
    // Party code only: Committee.party_affinity_* is not on every API the page
    // can be served with; the ranked list carries affinity.
    party: node.party_affiliation || node.party,
    affinityDem: null,
    affinityRep: null,
    amount: properties.total_amount == null ? null : Number(properties.total_amount),
    count: properties.payment_count == null ? null : Number(properties.payment_count),
    purposes: properties.purposes,
  })), profile.client_count, profile.total_received);
}

function vendorClientsHtml(list: ClientList | null, degree: number): string {
  if (!list) return deferredRelationshipMessage(degree);
  const table = relationshipTable(['Committee', 'Type', 'Paid', 'Payments', 'Purposes'], list.rows.map(row => [
    entityLink('Committee', row.id, row.name || 'Unknown committee'),
    displayValue(row.committeeType),
    formatCurrency(row.amount),
    formatCount(row.count),
    displayValue(row.purposes)
  ]), [2, 3]);
  return list.rows.length ? `${table}${listFooter(list.rows.length, list.total, 'committees')}` : table;
}

/** Sankey | Table toggle on the clients table; on any failure the table stays as it is. */
async function mountVendorSankey(profile: VendorNode, list: ClientList | null): Promise<void> {
  const section = document.getElementById('clients');
  const heading = section?.querySelector<HTMLElement>('h2');
  const table = section?.querySelector<HTMLElement>('table.leaf-relationship-list');
  if (!list || !section || !heading || !table) return;
  const { nodes, links } = vendorFlow({ id: profile.id, name: profile.name || 'Vendor' }, list);
  if (!links.length) return;
  try {
    await loadMoneyFlow();
    window.electinfoMoneyFlow?.mount({ section, heading, list: table, nodes, links, side: 'in' });
  } catch (err) {
    console.warn('vendor clients Sankey not rendered', err);
  }
}

export async function loadVendorDetail(id: string): Promise<void> {
  try {
    const profile = await loadLeafEntity<VendorNode>('vendors', id, VENDOR_PROFILE_QUERY, 'vendors');
    if (!profile) return renderDetailError('Vendor', 'No campaign vendor was found with this ID.');

    const [clients, purposes] = await Promise.all([
      loadVendorClients(profile),
      // Older API/graph versions must not prevent rendering the vendor profile.
      fetchPurposeFlow(profile.id, currentCycle()).catch(() => null),
    ]);
    const clientsHtml = vendorClientsHtml(clients, Number(profile.client_count || 0));

    renderDetailPage(profile.name || `Vendor ${profile.id}`, 'Campaign Vendor', [
      { id: 'overview', title: 'Overview', html: definitionList([
        ['Name', profile.name], ['Normalized name', profile.name_normalized],
        ['Location', [profile.city, profile.state].filter(Boolean).join(', ')], [purposes?.groups.length ? 'Top purposes' : 'Purpose categories', purposeSummary(purposes)],
        ['Cycles', profile.cycles], ['First payment', formatDate(profile.first_payment)], ['Last payment', formatDate(profile.last_payment)]
      ]) },
      { id: 'activity', title: 'Payment Activity', html: definitionList([
        ['Total received', formatCurrency(profile.total_received)], ['Payments', formatCount(profile.payment_count)],
        ['Committee clients', formatCount(profile.client_count)]
      ]) },
      { id: 'purposes', title: 'Spending by Purpose', html: '' },
      { id: 'clients', title: `Top Committee Clients (up to ${RELATIONSHIP_LIMIT})`, html: clientsHtml }
    ], 'vendors', 'Browse Campaign Vendors');
    mountVendorPurposes({ id: profile.id, name: profile.name || 'Vendor', cycles: profile.cycles, raw: profile.purpose_categories }, purposes);
    void mountVendorSankey(profile, clients);
  } catch (error) {
    renderDetailError('Vendor', errorMessage(error));
  }
}

export async function loadBlockDetail(id: string): Promise<void> {
  try {
    // The block map links /blocks/<geoid>/ (its GeoJSON has no ids).
    const blockId = isBlockGeoid(id) ? blockIdFromGeoid(id) : id;
    const block = await loadLeafEntity<BlockNode>('blocks', blockId, BLOCK_QUERY, 'blocks');
    if (!block) return renderDetailError('Census Block', 'No census block was found with this ID.');

    const countyRows = block.inCountyCountiesConnection.edges.map(({ node }) => [
      entityLink('County', node.id, node.name || node.id), displayValue(node.state), displayValue(node.county_fips)
    ]);
    const districtRows = block.inDistrictDistrictsConnection.edges.map(({ node }) => [
      entityLink('District', node.id, node.name || `${node.state}-${node.district_number}`),
      displayValue(node.state), displayValue(node.district_number)
    ]);
    const displayName = `Block ${block.block || block.geoid || block.id}`;

    renderDetailPage(displayName, 'Census Block', [
      { id: 'overview', title: 'Overview', html: definitionList([
        ['GEOID', block.geoid], ['State', block.state?.toUpperCase()], ['State FIPS', block.state_fips],
        ['County FIPS', block.county_fips], ['Tract', block.tract], ['Block', block.block]
      ]) },
      { id: 'geography', title: 'Geography', html:
        `<h3>County</h3>${relationshipTable(['County', 'State', 'FIPS'], countyRows)}` +
        `<h3>Congressional District</h3>${relationshipTable(['District', 'State', 'Number'], districtRows)}` },
      { id: 'registration', title: 'Voter Registration', html: relationshipTable(['Group', 'Registered'], [
        ['Total', formatCount(block.reg_total)], ['Democratic', formatCount(block.reg_dem)], ['Republican', formatCount(block.reg_rep)],
        ['Independent', formatCount(block.reg_ind)], ['Other', formatCount(block.reg_oth)], ['Male', formatCount(block.reg_male)],
        ['Female', formatCount(block.reg_female)], ['White', formatCount(block.reg_white)], ['Hispanic', formatCount(block.reg_hispanic)],
        ['Black', formatCount(block.reg_black)], ['Asian', formatCount(block.reg_asian)]
      ], [1]) },
      { id: 'turnout', title: '2022 General Election Turnout', html: relationshipTable(['Group', 'Voted', 'Turnout'], [
        ['Total', formatCount(block.gen2022_voted), formatPercent(block.gen2022_turnout_pct)],
        ['White', formatCount(block.gen2022_voted_white), formatPercent(block.gen2022_turnout_pct_white)],
        ['Hispanic', formatCount(block.gen2022_voted_hispanic), formatPercent(block.gen2022_turnout_pct_hispanic)],
        ['Black', formatCount(block.gen2022_voted_black), formatPercent(block.gen2022_turnout_pct_black)],
        ['Asian', formatCount(block.gen2022_voted_asian), formatPercent(block.gen2022_turnout_pct_asian)],
        ['Male', formatCount(block.gen2022_voted_male), formatPercent(block.gen2022_turnout_pct_male)],
        ['Female', formatCount(block.gen2022_voted_female), formatPercent(block.gen2022_turnout_pct_female)]
      ], [1, 2]) }
    ], 'blocks', 'Browse Census Blocks');
    registerGraphTarget({ kind: 'node', label: 'Block', id: block.id, title: displayName });
  } catch (error) {
    renderDetailError('Census Block', errorMessage(error));
  }
}

export async function loadPrecinctDetail(id: string): Promise<void> {
  try {
    const precinct = await loadLeafEntity<PrecinctNode>('precincts', id, PRECINCT_QUERY, 'precincts');
    if (!precinct) return renderDetailError('Voting Precinct', 'No voting precinct was found with this ID.');

    const countyRows = precinct.inCountyCountiesConnection.edges.map(({ node }) => [
      entityLink('County', node.id, node.name || node.id), displayValue(node.state), displayValue(node.county_fips)
    ]);
    const marginRatio = Number(precinct.pct_dem_lead || 0);
    const marginLabel = marginRatio === 0
      ? 'Tie'
      : `${marginRatio > 0 ? 'D' : 'R'}+${Math.abs(marginRatio * 100).toFixed(1)}%`;
    const displayName = precinct.precinct_id || precinct.geoid || precinct.id;

    renderDetailPage(displayName, 'Voting Precinct', [
      { id: 'overview', title: 'Overview', html: definitionList([
        ['GEOID', precinct.geoid], ['Precinct ID', precinct.precinct_id], ['State', precinct.state],
        ['State FIPS', precinct.state_fips], ['County FIPS', precinct.county_fips],
        ['Official boundary', precinct.official_boundary === null ? '—' : precinct.official_boundary ? 'Yes' : 'No']
      ]) },
      { id: 'results', title: '2024 Presidential Election Results', html: relationshipTable(['Result', 'Votes'], [
        ['Democratic', formatCount(precinct.votes_dem)], ['Republican', formatCount(precinct.votes_rep)],
        ['Total', formatCount(precinct.votes_total)], ['Margin', escapeHtml(marginLabel)]
      ], [1]) },
      { id: 'county', title: 'County', html: relationshipTable(['County', 'State', 'FIPS'], countyRows) }
    ], 'precincts', 'Browse Voting Precincts');
    registerGraphTarget({ kind: 'node', label: 'Precinct', id: precinct.id, title: displayName });
  } catch (error) {
    renderDetailError('Voting Precinct', errorMessage(error));
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
